import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth, useUser, IS_DEMO_MODE } from '@/lib/auth';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Loader2, ArrowLeft, Save, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000';

export function PromptEditPage() {
  const { slug, name } = useParams<{ slug: string; name: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.publicMetadata?.role === 'admin';

  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);

  const promptQuery = useQuery({
    queryKey: ['prompt', slug, name],
    queryFn: async () => {
      const token = await getToken();
      const resp = await fetch(`${API_BASE_URL}/api/clients/${slug}/settings/prompts/${name}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!resp.ok) throw new Error('Prompt not found');
      const data = (await resp.json()) as { name: string; content: string; version: number; model_target: string; description: string };
      setContent(data.content);
      return data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const resp = await fetch(`${API_BASE_URL}/api/clients/${slug}/settings/prompts/${name}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ content }),
      });
      if (!resp.ok) throw new Error('Save failed');
      return resp.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prompts', slug] });
      qc.invalidateQueries({ queryKey: ['prompt', slug, name] });
      setError(null);
      navigate(`/clients/${slug}/settings`);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Save failed'),
  });

  if (!isAdmin) {
    return (
      <div className="text-sm bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-2 text-amber-900">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>Édition réservée aux administrateurs Ducens.</span>
      </div>
    );
  }

  if (promptQuery.isPending) {
    return (
      <div className="flex items-center gap-2 text-ducens-muted">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
      </div>
    );
  }

  if (promptQuery.isError) {
    return (
      <div className="text-sm bg-red-50 border border-red-200 rounded-md p-4 flex items-start gap-2 text-red-900">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>Prompt introuvable.</span>
      </div>
    );
  }

  const data = promptQuery.data;
  const isDirty = data.content !== content;

  return (
    <>
      <div className="mb-6">
        <Link
          to={`/clients/${slug}/settings`}
          className="text-sm text-ducens-muted hover:text-ducens-ink flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Retour aux réglages
        </Link>
        <h1 className="text-2xl font-semibold leading-tight font-mono">{data.name}</h1>
        <p className="text-sm text-ducens-muted mt-1">
          {data.description} · modèle <code className="px-1 py-0.5 bg-ducens-bg rounded">{data.model_target}</code> ·
          version actuelle v{data.version}
        </p>
      </div>

      {IS_DEMO_MODE && (
        <div className="bg-ducens-accent/30 border border-ducens-accent rounded-md p-3 text-sm text-ducens-ink mb-4">
          Mode démo : sauvegarde simulée, rien n'est écrit en base.
        </div>
      )}

      <div className="bg-white border border-ducens-border rounded-lg p-4">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={20}
          className="w-full text-sm font-mono border border-ducens-border rounded-md p-3 resize-y focus:outline-none focus:border-ducens-ink leading-relaxed"
          placeholder="Contenu du prompt..."
        />
        <div className="flex items-center gap-2 mt-3">
          <button
            type="button"
            disabled={!isDirty || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 rounded-md bg-ducens-ink text-white text-sm font-medium',
              'hover:opacity-90 disabled:opacity-50'
            )}
          >
            {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Créer v{data.version + 1}
          </button>
          <Link
            to={`/clients/${slug}/settings`}
            className="px-3 py-2 rounded-md border border-ducens-border text-sm hover:bg-ducens-bg"
          >
            Annuler
          </Link>
          {isDirty && (
            <span className="text-xs text-ducens-muted ml-auto">Modifications non sauvegardées</span>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 text-sm bg-red-50 border border-red-200 rounded-md p-3 text-red-900">{error}</div>
      )}

      <div className="mt-6 text-xs text-ducens-muted bg-ducens-bg border border-ducens-border rounded-md p-3">
        <strong>Versionnement</strong> : sauvegarder crée la v{data.version + 1}, l'ancienne v{data.version} reste
        disponible mais désactivée. Aucun rollback automatique : tu peux réactiver une ancienne version manuellement
        si besoin.
      </div>
    </>
  );
}
